/**
 * Demand Explorer screen (task 14.3, R5, R7.2, R7.5, R7.6, R7.7; UI spec §5.2).
 *
 * The Field_Force_Demand_Driver breakdown, in two columns: a table the reader
 * regroups by Initiative, FF role, Activity_Type or Region on the left, and the
 * annual workload heatmap plus the formula disclosure on the right.
 *
 * ## The table foots, and the footing is not a sum of the rows
 *
 * R5.7's Reconciliation_Controls are the point of this screen: the grouped rows are
 * only trustworthy if they add up to the totals the engine reports independently.
 * The `Total` row therefore carries the endpoint's own `totalDemandHours` and a
 * distinct Initiative count taken across the drivers, so it is the figure the rows
 * reconcile *against* rather than a restatement of them. The three controls
 * themselves are reported in the footer, each named, because they fail for
 * different reasons.
 *
 * ## The heatmap is Platform × period, derived from two marginals
 *
 * Nothing stores demand per Platform *and* period; the wire carries the two
 * independent splits. `derivePlatformHeatmap` reconstructs the matrix from them —
 * see its own doc comment for why that is exact rather than an estimate.
 *
 * ## Grouped by Initiative, the rows open the Detail_Panel
 *
 * A row that names an Initiative is that Initiative, so it opens the same drawer the
 * Initiative list opens, on the same terms: a nested route
 * (`/manpower/demand-explorer/initiative/:id`) rendered through the `<Outlet />`
 * below, so this screen stays mounted and its grouping and filters are still applied
 * when the panel closes (R11.13). Under the other three groupings a row aggregates
 * many Initiatives and there is nothing single to open, so it carries no affordance
 * — see {@link BreakdownTable}.
 *
 * Capability: `manpower:read` to view (R7.2, R7.6, R7.7). The gate renders a
 * no-access notice rather than returning before the hooks, so the hook order is
 * stable while the session query resolves.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { usePageChrome } from '@/components/layout/page-chrome';
import { hasCapability, useSession } from '@/app/session/session';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CapacityStateBadge } from '@/components/ui/capacity-state-badge';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FilterActions, FilterBar, FilterSelect } from '@/shared/components/filter-bar';
import { isPlainRowActivation } from '@/shared/components/row-activation';
import { cn } from '@/lib/utils';
import { useManpowerReferenceSet, toNameLookup } from './manpower-reference-data';
import { useDemandExplorer, type ReconciliationClauses } from './demand-explorer/demand-explorer-data';
import {
  deriveExplorerRows,
  deriveExplorerTotals,
  derivePlatformHeatmap,
  type DemandExplorerRow,
  type DemandExplorerTotals,
  type GroupBy,
  type PlatformHeatmap,
} from './demand-explorer/demand-explorer-model';
import { FormulaTransparencyBlock } from './shared/FormulaTransparencyBlock';
import { currentPlanningYear, formatHours, planningYearOptions } from './shared/manpower-format';
import { FF_ROLE_OPTIONS } from './ff-role-options';

/** Every FF role at once — the endpoint's "omit `ffRole`". */
const ALL_ROLES = 'all';
/** Every Region at once. */
const ALL_REGIONS = 'all';

/**
 * The share of the busiest cell above which a heatmap cell prints its figure.
 *
 * Below it the fill is too pale to hold legible text, so the number lives in the
 * cell's hover text instead. A dense twelve-column row of small numerals is also
 * unreadable, so printing only the peaks is what makes the pattern visible.
 */
const HEATMAP_LABEL_THRESHOLD = 0.45;

export function DemandExplorerPage(): React.JSX.Element {
  const { t } = useTranslation();
  const session = useSession();
  const canRead = hasCapability(session.data, 'manpower:read');

  usePageChrome({
    title: t('manpower.demandExplorer.title'),
    subtitle: t('manpower.demandExplorer.subtitle'),
  });

  const [planningYear, setPlanningYear] = useState<number>(() => currentPlanningYear());
  const [granularity, setGranularity] = useState<'month' | 'calendar-week'>('month');
  const [groupBy, setGroupBy] = useState<GroupBy>('initiative');
  const [ffRole, setFfRole] = useState<string>(ALL_ROLES);
  const [regionId, setRegionId] = useState<string>(ALL_REGIONS);

  const demandQuery = useDemandExplorer(
    {
      planningYear,
      granularity,
      ...(ffRole !== ALL_ROLES && { ffRole }),
      ...(regionId !== ALL_REGIONS && { regionId }),
    },
    { enabled: canRead },
  );

  const regionsQuery = useManpowerReferenceSet('region', { enabled: canRead });
  const activityTypesQuery = useManpowerReferenceSet('ff-activity-type', { enabled: canRead });
  const platformsQuery = useManpowerReferenceSet('platform', { enabled: canRead });

  const regions = regionsQuery.data ?? [];
  const activityTypes = activityTypesQuery.data ?? [];

  const lookups = useMemo(
    () => ({
      activityTypes: toNameLookup(activityTypesQuery.data),
      regions: toNameLookup(regionsQuery.data),
    }),
    [activityTypesQuery.data, regionsQuery.data],
  );

  const drivers = useMemo(() => demandQuery.data?.drivers ?? [], [demandQuery.data]);
  const totalDemandHours = demandQuery.data?.totalDemandHours ?? 0;

  const rows = useMemo(
    () => deriveExplorerRows(drivers, groupBy, totalDemandHours, lookups),
    [drivers, groupBy, totalDemandHours, lookups],
  );
  const totals = useMemo(
    () => deriveExplorerTotals(drivers, totalDemandHours),
    [drivers, totalDemandHours],
  );
  const heatmap = useMemo(
    () =>
      derivePlatformHeatmap(
        drivers,
        toNameLookup(platformsQuery.data),
        new Map((platformsQuery.data ?? []).map((record) => [record.id, record.colour])),
      ),
    [drivers, platformsQuery.data],
  );

  if (session.isSuccess && !canRead) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <p className="text-muted-foreground text-[12.3px]">
          {t('manpower.demandExplorer.noAccess')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <FilterBar label={t('manpower.demandExplorer.filterBarLabel')}>
        <FilterSelect
          label={t('manpower.demandExplorer.groupBy')}
          value={groupBy}
          onChange={(value) => setGroupBy(value as GroupBy)}
        >
          <option value="initiative">{t('manpower.demandExplorer.byInitiative')}</option>
          <option value="ff-role">{t('manpower.demandExplorer.byRole')}</option>
          <option value="activity-type">{t('manpower.demandExplorer.byActivityType')}</option>
          <option value="region">{t('manpower.demandExplorer.byRegion')}</option>
        </FilterSelect>

        <FilterSelect
          label={t('manpower.demandExplorer.filterRole')}
          value={ffRole}
          onChange={setFfRole}
        >
          <option value={ALL_ROLES}>{t('manpower.demandExplorer.allRolesCombined')}</option>
          {FF_ROLE_OPTIONS.map((role) => (
            <option key={role.id} value={role.id}>
              {role.label}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          label={t('manpower.demandExplorer.filterRegion')}
          value={regionId}
          onChange={setRegionId}
        >
          <option value={ALL_REGIONS}>{t('manpower.demandExplorer.allRegions')}</option>
          {regions.map((region) => (
            <option key={region.id} value={region.id}>
              {region.name}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          label={t('manpower.demandExplorer.planningYear')}
          value={String(planningYear)}
          onChange={(value) => setPlanningYear(Number(value))}
        >
          {planningYearOptions().map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </FilterSelect>

        <FilterSelect
          label={t('manpower.demandExplorer.granularity')}
          value={granularity}
          onChange={(value) => setGranularity(value as 'month' | 'calendar-week')}
        >
          <option value="month">{t('manpower.demandExplorer.monthly')}</option>
          <option value="calendar-week">{t('manpower.demandExplorer.weekly')}</option>
        </FilterSelect>

        <FilterActions>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setPlanningYear(currentPlanningYear());
              setGranularity('month');
              setGroupBy('initiative');
              setFfRole(ALL_ROLES);
              setRegionId(ALL_REGIONS);
            }}
          >
            {t('common.reset')}
          </Button>
        </FilterActions>
      </FilterBar>

      {activityTypes.length === 0 && activityTypesQuery.isSuccess && (
        <Alert>
          <AlertTitle>{t('manpower.demandExplorer.noActivityTypesTitle')}</AlertTitle>
          <AlertDescription>{t('manpower.demandExplorer.noActivityTypes')}</AlertDescription>
        </Alert>
      )}

      {demandQuery.isSuccess && !demandQuery.data.reconciliationHolds && (
        <Alert variant="destructive">
          <AlertTitle>{t('manpower.demandExplorer.reconciliationTitle')}</AlertTitle>
          <AlertDescription>{t('manpower.demandExplorer.reconciliationBody')}</AlertDescription>
        </Alert>
      )}

      {demandQuery.isError && (
        <Alert variant="destructive">
          <AlertTitle>{t('common.error')}</AlertTitle>
          <AlertDescription>{t('manpower.demandExplorer.loadError')}</AlertDescription>
        </Alert>
      )}

      {/* The breakdown and its supporting panels side by side (UI spec §5.2): the
          table is the thing being read, the heatmap and the formula block explain
          it. `items-start` keeps each column its own height rather than stretching
          the shorter one down a long table; they stack below `lg`, where a
          four-column table needs the full width to itself. */}
      <div className="grid items-start gap-4 lg:grid-cols-[1.5fr_1fr]">
        <Card className="gap-4 p-5">
          {demandQuery.isPending ? (
            <p className="text-muted-foreground text-[12.3px]">{t('common.loading')}</p>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground text-[12.3px]">
              {t('manpower.demandExplorer.noData')}
            </p>
          ) : (
            <BreakdownTable rows={rows} totals={totals} groupBy={groupBy} />
          )}
        </Card>

        <div className="flex min-w-0 flex-col gap-4">
          {heatmap.rows.length > 0 && <WorkloadHeatmap heatmap={heatmap} />}
          <FormulaTransparencyBlock
            allocationMethod={t('manpower.demandExplorer.allocationMethod')}
          />
        </div>
      </div>

      {demandQuery.isSuccess && (
        <ReconciliationFooter clauses={demandQuery.data.reconciliation} />
      )}

      {/* The Detail_Panel (`/manpower/demand-explorer/initiative/:id`) mounts here
          without unmounting this screen, so the grouping, filters and planning year
          behind it are untouched. It renders into a portal as a modal drawer, so this
          position in the tree carries no layout meaning - only the mount point
          matters. Deliberately inside the capability-gated return: a reader without
          `manpower:read` never reaches this screen's rows, so they have no route into
          the panel from here either. */}
      <Outlet />
    </div>
  );
}

/**
 * The re-groupable breakdown, footed by the totals the rows must reconcile to (R5.7).
 *
 * ## Only the Initiative grouping has rows that open something
 *
 * `row.initiativeId` is non-null exactly when the reader is grouping by Initiative
 * (see {@link DemandExplorerRow}); under FF role, Activity_Type or Region a row is a
 * roll-up of many Initiatives and there is no single record behind it. Both the
 * pointer affordance and the link are therefore conditional rather than always
 * rendered: a hover highlight and a cursor on a row that cannot be opened is a
 * promise the screen does not keep.
 */
function BreakdownTable({
  rows,
  totals,
  groupBy,
}: {
  readonly rows: readonly DemandExplorerRow[];
  readonly totals: DemandExplorerTotals;
  readonly groupBy: GroupBy;
}): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Table>
      <caption className="sr-only">{t('manpower.demandExplorer.tableCaption')}</caption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col" className="text-[10.5px] uppercase">
            {t(`manpower.demandExplorer.column.${groupBy}`)}
          </TableHead>
          <TableHead scope="col" className="text-right text-[10.5px] uppercase">
            {t('manpower.demandExplorer.initiatives')}
          </TableHead>
          <TableHead scope="col" className="text-right text-[10.5px] uppercase">
            {t('manpower.demandExplorer.demandHours')}
          </TableHead>
          <TableHead scope="col" className="text-[10.5px] uppercase">
            {t('manpower.demandExplorer.share')}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          // Resolved once per row and shared by the handler and the link below, so the
          // two can never drift to different targets.
          const panelPath = row.initiativeId === null ? null : detailPanelPath(row.initiativeId);

          return (
          <TableRow
            key={row.key}
            {...(panelPath !== null && {
              onClick: (event) => {
                if (isPlainRowActivation(event)) {
                  navigate(panelPath);
                }
              },
              // UI spec §3.5's clickable-row treatment, the same one the Initiative
              // list uses: `--accent-soft` on hover plus a pointer, which is what
              // distinguishes a row that opens something from the Table primitive's
              // default read-only highlight.
              className: 'hover:bg-accent-soft cursor-pointer',
            })}
          >
            <TableCell className="max-w-[280px] truncate text-[12.3px]" title={row.groupLabel}>
              {/* The group name, and — when the group is one Initiative — the only
                  keyboard route into its Detail_Panel. The row's click handler above
                  is a pointer convenience over this anchor, never a replacement for
                  it: a `<tr>` is not focusable, so without the link the panel would
                  be openable by mouse alone (R18.6) and the browser's own
                  open-in-new-tab and copy-address behaviour would be gone too.

                  Styled as the cell's own text rather than as an accent link, for the
                  reason the Initiative list gives: the whole row opens the same
                  target, so an underlined label would advertise a second, narrower
                  affordance. The focus ring stays explicit because it is then the
                  only thing that reveals the element to a keyboard reader. */}
              {panelPath === null ? (
                row.groupLabel
              ) : (
                <Link
                  to={panelPath}
                  className="focus-visible:outline-accent-line rounded-[var(--radius-control)] focus-visible:outline-2 focus-visible:outline-offset-1"
                >
                  {row.groupLabel}
                </Link>
              )}
              {/* R5.9's excluded drivers, kept on the row rather than in a column of
                  their own: it is an exception on a handful of rows, so a whole
                  column would be empty for most of the table while costing the
                  width the group name needs. */}
              {row.dataIncompleteCount > 0 && (
                <CapacityStateBadge
                  state="data_incomplete"
                  className="ml-2"
                  aria-label={t('manpower.demandExplorer.dataIncompleteAria', {
                    count: row.dataIncompleteCount,
                  })}
                >
                  {row.dataIncompleteCount}
                </CapacityStateBadge>
              )}
            </TableCell>
            <TableCell className="text-right text-[12.3px] tabular-nums">
              {row.initiativeCount}
            </TableCell>
            <TableCell className="text-right text-[12.3px] font-medium tabular-nums">
              {formatHours(row.demandHours)}
            </TableCell>
            <TableCell>
              <ShareBar percent={row.sharePercent} />
            </TableCell>
          </TableRow>
          );
        })}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell className="text-[12.3px] font-semibold">
            {t('manpower.demandExplorer.total')}
          </TableCell>
          <TableCell className="text-right text-[12.3px] font-semibold tabular-nums">
            {totals.initiativeCount}
          </TableCell>
          <TableCell className="text-right text-[12.3px] font-semibold tabular-nums">
            {formatHours(totals.demandHours)}
          </TableCell>
          <TableCell className="text-[12.3px] font-semibold tabular-nums">
            {t('manpower.percent', { value: '100' })}
          </TableCell>
        </TableRow>
      </TableFooter>
    </Table>
  );
}

/**
 * The Detail_Panel route for an Initiative, nested under this screen
 * (`/manpower/demand-explorer/initiative/:id`) so the panel opens *here* rather than
 * sending the reader to the Initiative list and losing the grouping, filters and
 * planning year they were reading.
 *
 * `initiative/:id` rather than a bare `:id`, matching the calendar's shape: the
 * segment says what the id identifies, which a path under a screen named after demand
 * rather than after Initiatives does not otherwise make obvious.
 *
 * No query string is carried over, unlike the Initiative list's equivalent: this
 * screen keeps its view state in component state rather than in the search params,
 * and the nested route leaves it mounted, so there is nothing in the URL to preserve.
 */
function detailPanelPath(initiativeId: string): string {
  return `/manpower/demand-explorer/initiative/${initiativeId}`;
}

/**
 * A group's share of the total, as a figure and a bar.
 *
 * Both, not just the bar: the bar makes the ranking readable at a glance and the
 * figure is what someone quotes. The bar carries `aria-hidden` because the
 * percentage beside it already says the same thing, and announcing it twice is
 * noise.
 */
function ShareBar({ percent }: { readonly percent: number }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <span className="flex items-center gap-2">
      <span className="text-[12.3px] tabular-nums">
        {t('manpower.percent', { value: percent.toFixed(1) })}
      </span>
      <span
        aria-hidden
        className="bg-line-soft relative block h-[10px] min-w-[60px] flex-1 overflow-hidden rounded-[2px]"
      >
        <span
          className="absolute inset-y-0 left-0 block rounded-[2px] bg-[var(--accent)]"
          style={{ width: `${String(Math.min(percent, 100))}%` }}
        />
      </span>
    </span>
  );
}

/**
 * The annual workload heatmap: one row per Platform, one column per period (R5.3,
 * R5.4, UI spec §5.2).
 *
 * A `table`, not a grid of `div`s: it is a two-dimensional matrix with headers on
 * both axes, which is what a table element means, and it is the only structure that
 * lets a screen reader say "P1, March, 1,141 hours" when reading a cell.
 */
function WorkloadHeatmap({ heatmap }: { readonly heatmap: PlatformHeatmap }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Card className="gap-1 p-5">
      <p className="text-txt text-[13px] font-semibold">
        {t('manpower.demandExplorer.heatmapHeading')}
      </p>
      <p className="text-muted-foreground mb-2 text-[11.5px]">
        {t('manpower.demandExplorer.heatmapSubtitle')}
      </p>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-[2px]">
          <caption className="sr-only">{t('manpower.demandExplorer.heatmapCaption')}</caption>
          <thead>
            <tr>
              <th scope="col" className="sr-only">
                {t('manpower.demandExplorer.heatmapPlatformColumn')}
              </th>
              {heatmap.periodLabels.map((label, index) => (
                <th
                  key={heatmap.periodIds[index]}
                  scope="col"
                  className="text-label text-[9.5px] font-semibold tracking-wide uppercase"
                >
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {heatmap.rows.map((row) => (
              <tr key={row.platformId}>
                <th
                  scope="row"
                  className="text-txt max-w-[120px] truncate pr-2 text-left text-[11.5px] font-normal"
                >
                  <span className="flex items-center gap-1.5">
                    <span
                      aria-hidden
                      className="block size-2.5 shrink-0 rounded-[2px]"
                      style={{ backgroundColor: row.colour ?? 'var(--grey)' }}
                    />
                    <span className="truncate">{row.label}</span>
                  </span>
                </th>
                {row.cells.map((cell) => (
                  <td
                    key={cell.periodId}
                    className="h-[22px] rounded-[2px] text-center text-[9.5px] font-semibold tabular-nums"
                    // The Platform's own colour at an opacity scaled to the cell's
                    // share of the busiest cell, so one hue reads as one Platform
                    // across the row while the shade carries the load. The stored
                    // colour is `#rrggbb` (migration 0002's CHECK), so appending two
                    // hex digits is a safe way to derive the tint without a colour
                    // library — the same trick the Initiative list's Platform tags use.
                    style={{
                      backgroundColor:
                        row.colour === null
                          ? `color-mix(in srgb, var(--grey) ${String(Math.round(cell.intensity * 70))}%, transparent)`
                          : `${row.colour}${alphaHex(cell.intensity)}`,
                    }}
                    title={t('manpower.demandExplorer.heatmapCellTooltip', {
                      platform: row.label,
                      period: cell.periodLabel,
                      hours: formatHours(cell.demandHours),
                    })}
                  >
                    {cell.intensity >= HEATMAP_LABEL_THRESHOLD ? formatHours(cell.demandHours) : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-muted-foreground mt-2 text-[11px]">
        {t('manpower.demandExplorer.heatmapLegend')}
      </p>
    </Card>
  );
}

/**
 * The three Reconciliation_Controls of R5.7, each named and each showing whether it
 * held for the figures on screen.
 *
 * Stated even when everything holds, which is the point: a control that is only
 * mentioned when it fails is indistinguishable from a control nobody runs. A broken
 * one is coloured *and* prefixed with a word, never colour alone (R10.6, R18.8).
 */
function ReconciliationFooter({
  clauses,
}: {
  readonly clauses: ReconciliationClauses;
}): React.JSX.Element {
  const { t } = useTranslation();

  const controls = [
    { key: 'initiativeDrivers', holds: clauses.initiativeDriversHold },
    { key: 'driverPeriods', holds: clauses.driverPeriodsHold },
    { key: 'roleTotals', holds: clauses.roleTotalsHold },
  ] as const;

  return (
    <p className="text-muted-foreground text-[11px]">
      <span className="font-semibold">{t('manpower.demandExplorer.reconciliationControls')}</span>{' '}
      {controls.map((control, index) => (
        <span key={control.key}>
          {index > 0 && <span aria-hidden>{' · '}</span>}
          <span className={cn(control.holds ? undefined : 'font-semibold text-[var(--risk)]')}>
            {t(`manpower.demandExplorer.control.${control.key}`)}
            {!control.holds && ` — ${t('manpower.demandExplorer.controlBroken')}`}
          </span>
        </span>
      ))}
    </p>
  );
}

/**
 * An intensity of 0-1 as a two-digit hex alpha suffix for an `#rrggbb` colour.
 *
 * Capped below full opacity so even the busiest cell keeps its printed figure
 * readable against the fill, and floored above zero so an empty cell still reads as
 * part of the row rather than as a gap in the table.
 */
function alphaHex(intensity: number): string {
  const MIN_ALPHA = 0.08;
  const MAX_ALPHA = 0.62;
  const alpha = MIN_ALPHA + intensity * (MAX_ALPHA - MIN_ALPHA);
  return Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
}
